import { FunctionComponent, useCallback, useState } from 'react'

import styled, { css } from 'styled-components'

import { mobileCSS } from '@measures/responsive'
import { type Image } from '@utils/cook/types'
import useFeatureFlagsSkeleton from '@hooks/useFeatureFlagsSkeleton'
import useIsAuthResolved from '@hooks/useIsAuthResolved'
import useUser from '@hooks/useUser'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import FollowMyTeamDrawer from '@components/FollowMyTeam/FollowMyTeamDrawer'
import FollowMyTeamOverviewComponent from '@components/FollowMyTeamOverview'
import FollowMyTeamOverviewBanner from '@components/FollowMyTeamOverview/FollowMyTeamOverviewBanner'

export interface FollowMyTeamOverviewAPIProps {
  title: string
  backgroundImage: Image<'16_9'>
  image: Image<'free'>
}

const FollowMyTeamOverviewWrapper = styled.div`
  height: 80px;

  ${mobileCSS(css`
    height: 64px;
  `)};
`

const FollowMyTeamOverview: FunctionComponent<FollowMyTeamOverviewAPIProps> = ({
  title,
  backgroundImage,
  image,
}) => {
  const { isFollowMyTeamEnabled } = useFeatureFlagsSkeleton()
  const user = useUser()
  const isAuthResolved = useIsAuthResolved()

  const [openAt, setOpenAt] = useState<
    'showSelectTeam' | 'showMyTeams' | undefined
  >(undefined)

  const handleBannerClick = useCallback(() => {
    setOpenAt('showSelectTeam')
  }, [])

  const handleSettingsClick = useCallback(() => {
    setOpenAt('showMyTeams')
  }, [])

  const handleAddTeamClick = useCallback(() => {
    setOpenAt('showSelectTeam')
  }, [])

  const handleCloseDrawer = useCallback(() => {
    setOpenAt(undefined)
  }, [])

  if (!isFollowMyTeamEnabled) {
    return null
  }

  return (
    <FollowMyTeamOverviewWrapper>
      {isAuthResolved ? (
        !!user ? (
          <FollowMyTeamOverviewComponent
            backgroundImage={backgroundImage}
            image={image}
            title={title}
            onBannerClick={handleBannerClick}
            onSettingsClick={handleSettingsClick}
            onAddTeamClick={handleAddTeamClick}
          />
        ) : (
          <FollowMyTeamOverviewBanner
            state="notLoggedIn"
            backgroundImage={backgroundImage}
            image={image}
            title={title}
            onClick={handleBannerClick}
          />
        )
      ) : null}
      <FollowMyTeamDrawer openAt={openAt} onClose={handleCloseDrawer} />
    </FollowMyTeamOverviewWrapper>
  )
}

const widget = {
  kind: ['follow-my-team-overview'],
  component: FollowMyTeamOverview,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
